import * as Cause from "effect/Cause";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Tracer from "effect/Tracer";
import type { HttpClient } from "effect/unstable/http";
import { OtlpSerialization, OtlpTracer } from "effect/unstable/observability";

export interface RelayClientTracingConfig {
  readonly tracesUrl: string;
  readonly tracesDataset: string;
  readonly tracesToken: string;
}

export interface RelayClientTracingResource {
  readonly serviceName: string;
  readonly serviceVersion?: string;
  readonly runtime: string;
  readonly client: string;
  readonly component?: string;
}

export class RelayClientTracer extends Context.Reference(
  "@cleo/shared/relayTracing/RelayClientTracer",
  {
    defaultValue: () => Option.none<Tracer.Tracer>(),
  },
) {}

export const withRelayClientTracing = <A, E, R>(
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<A, E, R> =>
  RelayClientTracer.pipe(
    Effect.flatMap(
      Option.match({
        onNone: () => effect,
        onSome: (tracer) => effect.pipe(Effect.provideService(Tracer.Tracer, tracer)),
      }),
    ),
  );

const SAFE_TRACE_NAME = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const SAFE_TRACE_ID = /^[A-Za-z0-9._:-]{1,128}$/;

function readTraceField(value: object, key: string): unknown {
  try {
    return (value as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function safeTraceName(value: unknown): string | undefined {
  return typeof value === "string" && SAFE_TRACE_NAME.test(value) ? value : undefined;
}

function traceStatus(value: object): number | undefined {
  const status = readTraceField(value, "status");
  if (typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599) {
    return status;
  }
  const response = readTraceField(value, "response");
  return typeof response === "object" && response !== null ? traceStatus(response) : undefined;
}

// Exported spans carry an error's message and stack, and any message or stack
// can quote a credential. A span error keeps only its tag or name, its HTTP
// status and its trace ID, for itself and each cause: telemetry carries no
// free text (W-b8299199).
function traceSafeError(value: unknown, seen = new WeakSet<object>()): Error {
  let name = "Error";
  let status: number | undefined;
  let traceId: string | undefined;
  let cause: Error | undefined;
  if (typeof value === "object" && value !== null) {
    name =
      safeTraceName(readTraceField(value, "_tag")) ??
      safeTraceName(readTraceField(value, "name")) ??
      name;
    status = traceStatus(value);
    const rawTraceId = readTraceField(value, "traceId");
    traceId =
      typeof rawTraceId === "string" && SAFE_TRACE_ID.test(rawTraceId) ? rawTraceId : undefined;
    if (!seen.has(value)) {
      seen.add(value);
      const nested = readTraceField(value, "cause");
      if (nested !== undefined) {
        cause = traceSafeError(nested, seen);
      }
    }
  }
  const details = [
    status !== undefined ? `status ${status}` : undefined,
    traceId !== undefined ? `traceId ${traceId}` : undefined,
  ]
    .filter((detail) => detail !== undefined)
    .join(", ");
  const error = new Error(details, cause ? { cause } : undefined);
  error.name = name;
  error.stack = `${name}${details ? `: ${details}` : ""}${cause ? `\nCaused by: ${cause.stack ?? cause.name}` : ""}`;
  return error;
}

function traceSafeExit(exit: Exit.Exit<unknown, unknown>): Exit.Exit<unknown, unknown> {
  if (Exit.isSuccess(exit)) {
    return exit;
  }
  return Exit.failCause(
    Cause.fromReasons(
      exit.cause.reasons.map((reason) => {
        if (Cause.isFailReason(reason)) {
          return Cause.makeFailReason(traceSafeError(reason.error));
        }
        if (Cause.isDieReason(reason)) {
          return Cause.makeDieReason(traceSafeError(reason.defect));
        }
        return reason;
      }),
    ),
  );
}

function nonInterferingTracer(delegate: Tracer.Tracer): Tracer.Tracer {
  return Tracer.make({
    span(options) {
      const span = delegate.span(options);
      const end = span.end.bind(span);
      span.end = (endTime, exit) => {
        try {
          end(endTime, traceSafeExit(exit));
        } catch {
          // Telemetry is best-effort and must never change application behavior.
        }
      };
      return span;
    },
    ...(delegate.context ? { context: delegate.context } : {}),
  });
}

export function makeRelayClientTracingLayer(
  config: RelayClientTracingConfig | null,
  resource: RelayClientTracingResource,
): Layer.Layer<never, never, HttpClient.HttpClient> {
  if (config === null) {
    return Layer.succeed(RelayClientTracer, Option.none());
  }

  const tracerLayer = OtlpTracer.layer({
    url: config.tracesUrl,
    headers: {
      Authorization: `Bearer ${config.tracesToken}`,
      "X-Axiom-Dataset": config.tracesDataset,
    },
    resource: {
      serviceName: resource.serviceName,
      serviceVersion: resource.serviceVersion,
      attributes: {
        "service.runtime": resource.runtime,
        "service.component": resource.component ?? "relay-client",
        "cleo.client.surface": resource.client,
      },
    },
  }).pipe(Layer.provide(OtlpSerialization.layerJson));

  return Layer.effect(
    RelayClientTracer,
    Tracer.Tracer.pipe(Effect.map(nonInterferingTracer), Effect.map(Option.some)),
  ).pipe(Layer.provide(tracerLayer));
}
